<template>
    <div class="search-results dropdown is-visible">
        <div class="dropdown-content dropdown-content-help">
            Press
            <v-icon icon="arrow-up"/>
            <v-icon icon="arrow-down"/>
            to navigate, <strong>enter</strong> to select,
            <strong>esc</strong> to dismiss
        </div>
        <div class="dropdown-content no-padding-top no-padding-bottom" :class="isScrollableResults"
             style="max-height:500px;">
            <div v-for="group in groups">
                <template v-if="group.results.length > 0">
                    <div class="dropdown-content dropdown-content-help text-left search-results-title">
                        <v-icon :icon="group.resource" class="inline-s icon-blue"/>
                        <h3 class="display-inline text-blue">{{group.resource}}</h3></div>
                    <div class="dropdown-content no-padding-top no-padding-bottom"
                         v-for="(value, index) in group.results.slice(0, maxResultsInDropdown)">
                        <div class="dropdown-item search-item cursor-default"
                             :class="{'search-item-highlighted': isHighlight(index, group)}">
                            <component :class="`isHighLight-${group.resource}-${index}`"
                                       :is="getComponent(group.resource)" :value="value"/>
                        </div>
                    </div>
                </template>
            </div>
            <div class="dropdown-content" v-if="hasZeroResults">
                <div class="dropdown-item">
                    No results found
                </div>
            </div>
        </div>
        <div class="dropdown-content dropdown-content-help cursor-pointer" @click="seeAllResults"
             v-if="!hasZeroResults && hasMoreResults">
            See all results
        </div>
    </div>
</template>

<script>
    import {vIcon} from '@/components/form';
    import CustomersTile from './components/tile-customers';
    import InvoicesTile from './components/tile-invoices';
    import OrdersTile from './components/tile-orders';
    import TransactionsTile from './components/tile-transactions';
    import capitalize from '@/common/helpers/capitalize';
    import Shortcuts from '@/common/shortcuts/shortcuts';

    export default {
        name: 'search-results',
        components: {
            vIcon,
            CustomersTile,
            InvoicesTile,
            OrdersTile,
            TransactionsTile,
        },
        props: {
            results: Object,
            query: Object,
        },
        data() {
            return {
                maxResultsInDropdown: 3,
                selectedResultIndex: -1,
                shortcuts: [],
            };
        },
        computed: {
            isScrollableResults() {
                return this.flattenShownResults.length > this.maxResultsInDropdown ? 'is-scrollable' : '';
            },
            groups() {
                return Object.entries(this.results)
                    .map(result => ({
                        resource: result[0],
                        results: result[1],
                    }));
            },
            hasMoreResults() {
                return Object.values(this.results)
                    .filter(result => result.length > this.maxResultsInDropdown).length > 0;
            },
            hasZeroResults() {
                return Object.values(this.results)
                    .filter(result => result.length > 0).length === 0;
            },
            flattenShownResults() {
                return [].concat(...Object.values(this.groups)
                    .map(group => group.results
                        .slice(0, this.maxResultsInDropdown)
                        .map(result => ({
                            resource: group.resource,
                            result,
                        }))));
            },
        },
        watch: {
            flattenShownResults() {
                this.selectedResultIndex = -1;
            },
        },
        methods: {
            isHighlight(index, result = false) {
                return result && this.isResultHighlight(index, result);
            },
            isResultHighlight(index, result) {
                if (this.selectedResultIndex >= 0) {
                    const selectedResult = this.flattenShownResults[this.selectedResultIndex];
                    const isHighlighted = selectedResult.resource === result.resource && result.results[index] === selectedResult.result;
                    if (isHighlighted) {
                        const element = document.querySelector(`.isHighLight-${result.resource}-${index}`);
                        if (element) {
                            // TODO move scroll method outside, make scroll on change, on in the render
                            element.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                        }
                    }
                    return isHighlighted;
                }
                return false;
            },
            seeAllResults() {
                const {q} = this.query;
                this.$router.push({
                    name: 'search-results',
                    query: {
                        q,
                    },
                });
            },
            getComponent(resource) {
                return `${capitalize(resource)}Tile`;
            },
            resultToRouteMap(selected) {
                const {resource, result} = selected;
                const resourceMap = {
                    customers: {
                        name: 'customer-details',
                        params: {id: result.id},
                    },
                    invoices: {
                        name: 'order-details',
                        params: {
                            id: result.subscriptionId,
                            invoice: result.id,
                        },
                    },
                    transactions: {
                        name: 'transaction-details',
                        params: {id: result.id},
                    },
                    orders: {
                        name: 'order-details',
                        params: {id: result.id},
                    },
                };
                return resourceMap[resource];
            },
            selectResult() {
                const selected = this.flattenShownResults[this.selectedResultIndex];
                if (selected) {
                    this.selectedResultIndex = -1;
                    this.$emit('clear');
                    this.$router.push(this.resultToRouteMap(selected));
                }
            },
        },
        created() {
            this.shortcuts = [
                Shortcuts.up.register((event) => {
                    event.preventDefault();
                    this.selectedResultIndex -= 1;
                    if (this.selectedResultIndex < 0) {
                        this.selectedResultIndex = this.flattenShownResults.length - 1;
                    }
                }),
                Shortcuts.down.register((event) => {
                    event.preventDefault();
                    this.selectedResultIndex += 1;
                    if (this.selectedResultIndex > this.flattenShownResults.length - 1) {
                        this.selectedResultIndex = 0;
                    }
                }),
                Shortcuts.enter.register((event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    this.selectResult();
                }),
                Shortcuts.tab.register((event) => {
                    event.preventDefault();
                    if (this.selectedResultIndex < 0) {
                        this.selectedResultIndex = 0;
                    }
                    this.selectResult();
                }),
                Shortcuts.esc.register(() => {
                    this.$emit('clear');
                }),
            ];
        },
        beforeDestroy() {
            this.shortcuts.forEach(shortcut => shortcut.unregister());
        },
    };
</script>
